import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Monitor,
  ShoppingCart,
  Zap,
  ChefHat,
  MonitorPlay,
  UtensilsCrossed,
  Sandwich,
  Tablet,
  MonitorSmartphone,
  Settings,
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useFeatureToggles } from '@/hooks/useFeatureToggles';
import { useTenantModules, type ModuleKey } from '@/hooks/useTenantModules';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export type HomeScreen =
  | 'dashboard'
  | 'caisse'
  | 'caisse-rapide'
  | 'caisse-restaurant'
  | 'cuisine'
  | 'salle-display'
  | 'tablette-serveur'
  | 'kiosque';

interface ScreenOption {
  key: HomeScreen;
  label: string;
  description: string;
  icon: React.ElementType;
  color: string;
  route: string;
  /** Module(s) required — at least one must be enabled */
  requiredModules: ModuleKey[];
  /** Always visible (no module check) */
  alwaysVisible?: boolean;
}

const ALL_SCREEN_OPTIONS: ScreenOption[] = [
  {
    key: 'caisse',
    label: 'Caisse classique',
    description: 'Écran de caisse complet avec toutes les fonctions',
    icon: ShoppingCart,
    color: 'from-blue-600 to-blue-800',
    route: '/caisse',
    requiredModules: ['mod_magasin'],
    alwaysVisible: true,
  },
  {
    key: 'dashboard',
    label: 'Tableau de bord',
    description: "Vue d'ensemble avec statistiques et raccourcis",
    icon: Monitor,
    color: 'from-emerald-600 to-emerald-800',
    route: '/dashboard',
    requiredModules: [],
    alwaysVisible: true,
  },
  {
    key: 'caisse-rapide',
    label: 'Caisse rapide',
    description: 'Interface simplifiée pour encaissements rapides',
    icon: Zap,
    color: 'from-amber-500 to-orange-600',
    route: '/caisse-rapide',
    requiredModules: ['mod_caisse_rapide'],
    alwaysVisible: false,
  },
  {
    key: 'caisse-restaurant',
    label: 'Caisse restaurant',
    description: 'Caisse avec plans de salle et gestion des tables',
    icon: UtensilsCrossed,
    color: 'from-rose-500 to-rose-700',
    route: '/caisse-restaurant',
    requiredModules: ['mod_restaurant_salle'],
  },
  {
    key: 'cuisine',
    label: 'Cuisine (KDS)',
    description: 'Affichage des commandes en cuisine',
    icon: ChefHat,
    color: 'from-red-500 to-red-700',
    route: '/cuisine',
    requiredModules: ['mod_cuisine_kds'],
  },
  {
    key: 'salle-display',
    label: 'Affichage salle',
    description: 'Suivi des commandes en attente pour la salle',
    icon: MonitorPlay,
    color: 'from-purple-500 to-purple-700',
    route: '/salle-display',
    requiredModules: ['mod_restaurant_salle'],
  },
  {
    key: 'tablette-serveur',
    label: 'Tablette serveur',
    description: 'Prise de commande mobile multi-tables',
    icon: Tablet,
    color: 'from-teal-500 to-teal-700',
    route: '/chr/serveur',
    requiredModules: ['mod_tablette_serveur'],
  },
  {
    key: 'kiosque',
    label: 'Borne kiosque',
    description: 'Commandes autonomes pour les clients',
    icon: MonitorSmartphone,
    color: 'from-indigo-500 to-indigo-700',
    route: '/kiosque',
    requiredModules: ['mod_kiosque'],
  },
];

/** Route map exported for AuthRedirect */
export const HOME_SCREEN_ROUTE_MAP: Record<HomeScreen, string> = {
  dashboard: '/dashboard',
  caisse: '/caisse',
  'caisse-rapide': '/caisse-rapide',
  'caisse-restaurant': '/caisse-restaurant',
  cuisine: '/cuisine',
  'salle-display': '/salle-display',
  'tablette-serveur': '/chr/serveur',
  kiosque: '/kiosque',
};

export default function HomeScreenSelector() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isEnabled } = useTenantModules();
  const { isFeatureEnabled } = useFeatureToggles();
  const [selected, setSelected] = useState<HomeScreen | null>(null);
  const [saving, setSaving] = useState(false);

  // Filter options based on enabled modules
  const visibleOptions = ALL_SCREEN_OPTIONS.filter((opt) => {
    // Caisse classique masquable par feature toggle (marchand caisse rapide only)
    if (opt.key === 'caisse' && !isFeatureEnabled('caisse_classique')) return false;
    if (opt.alwaysVisible) return true;
    // At least one of the required modules must be enabled
    return opt.requiredModules.some((mod) => isEnabled(mod));
  });

  const handleConfirm = async () => {
    if (!selected || !user) return;
    setSaving(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({ home_screen: selected } as any)
        .eq('user_id', user.id);
      if (error) throw error;
      navigate(HOME_SCREEN_ROUTE_MAP[selected], { replace: true });
    } catch (e: any) {
      console.error('HomeScreenSelector save error:', e);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 p-4">
      <div className="w-full max-w-4xl">
        <div className="mb-8 text-center">
          <h1 className="mb-2 text-3xl font-bold text-white">Bienvenue sur VisionPos 360</h1>
          <p className="text-lg text-gray-400">Choisissez votre écran d'accueil par défaut</p>
          <p className="mt-1 text-sm text-gray-500">
            Vous pourrez modifier ce choix dans Configuration &gt; Mode kiosque
          </p>
        </div>

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {visibleOptions.map((opt) => {
            const Icon = opt.icon;
            const isSelected = selected === opt.key;
            return (
              <button
                key={opt.key}
                onClick={() => setSelected(opt.key)}
                className={cn(
                  'relative rounded-xl border-2 p-5 text-left transition-all duration-200',
                  isSelected
                    ? 'scale-[1.02] border-white bg-white/10 shadow-xl'
                    : 'border-transparent bg-white/5 hover:border-white/20 hover:bg-white/10',
                )}
              >
                <div
                  className={cn(
                    'mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br',
                    opt.color,
                  )}
                >
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <h3 className="text-base font-semibold text-white">{opt.label}</h3>
                <p className="mt-1 text-sm text-gray-400">{opt.description}</p>
                {isSelected && (
                  <div className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-white">
                    <div className="h-3 w-3 rounded-full bg-blue-600" />
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex justify-center">
          <button
            onClick={handleConfirm}
            disabled={!selected || saving}
            className={cn(
              'rounded-lg px-8 py-3 text-base font-semibold transition-all',
              selected
                ? 'bg-white text-gray-900 shadow-lg hover:bg-gray-100'
                : 'cursor-not-allowed bg-gray-700 text-gray-400',
            )}
          >
            {saving ? 'Enregistrement...' : 'Confirmer'}
          </button>
        </div>
      </div>
    </div>
  );
}
